var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@apollo/client';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import GET_ORDER_DELIVERY_STATUS_AUDITS from 'api/queries/GET_ORDER_DELIVERY_STATUS_AUDITS';
import Button from 'layout/atoms/Button/Button';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import Modal from 'layout/atoms/Modal/Modal';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import { selectModalByName } from 'store/layoutSlice';
import { selectOrderDetailsRefetchFlags } from 'store/orderDetailsSlice';
const PAGE_SIZE = 20;
function DeliveryStatusAuditHistory() {
    var _a;
    const { t } = useTranslation(['orders']);
    const modalData = (_a = useSelector(selectModalByName('deliveryStatusAuditHistory'))) === null || _a === void 0 ? void 0 : _a.data;
    const orderId = modalData === null || modalData === void 0 ? void 0 : modalData.orderId;
    const deliveriesChangedFlag = useSelector(selectOrderDetailsRefetchFlags).fetchOrderVolume;
    const [audits, setAudits] = useState([]);
    const [pageInfo, setPageInfo] = useState({
        hasNextPage: false,
        endCursor: null,
    });
    const { data, loading, refetch, fetchMore } = useQuery(GET_ORDER_DELIVERY_STATUS_AUDITS, {
        variables: { id: orderId, first: PAGE_SIZE },
        skip: !orderId,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
    });
    useEffect(() => {
        if (deliveriesChangedFlag && orderId) {
            refetch({ id: orderId, first: PAGE_SIZE });
        }
    }, [deliveriesChangedFlag, orderId, refetch]);
    useEffect(() => {
        var _a;
        const connection = (_a = data === null || data === void 0 ? void 0 : data.node) === null || _a === void 0 ? void 0 : _a.deliveryStatusAudits;
        if (!connection)
            return;
        setAudits(connection.edges.map((edge) => edge.node));
        setPageInfo({
            hasNextPage: connection.pageInfo.hasNextPage,
            endCursor: connection.pageInfo.endCursor,
        });
    }, [data]);
    const handleLoadMore = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        if (!pageInfo.hasNextPage)
            return;
        const result = yield fetchMore({
            variables: { id: orderId, first: PAGE_SIZE, after: pageInfo.endCursor },
        });
        const connection = (_b = (_a = result.data) === null || _a === void 0 ? void 0 : _a.node) === null || _b === void 0 ? void 0 : _b.deliveryStatusAudits;
        if (!connection)
            return;
        setAudits((prev) => [...prev, ...connection.edges.map((edge) => edge.node)]);
        setPageInfo({
            hasNextPage: connection.pageInfo.hasNextPage,
            endCursor: connection.pageInfo.endCursor,
        });
    });
    const statusLabel = (status) => status === 'COLLECTED'
        ? t('delivery_status_history_status_collected')
        : t('delivery_status_history_status_planned');
    return (React.createElement(Modal, { alwaysScroll: true, name: "deliveryStatusAuditHistory", size: "m", header: React.createElement(Label, { weight: "strong" }, t('delivery_status_history')) },
        React.createElement(Grid, { columns: 1, gap: 16 },
            loading && !data && React.createElement(Loader, null),
            !loading && audits.length === 0 && (React.createElement(EmptyState, { noHeader: true, subheader: t('delivery_status_history_empty') })),
            audits.map((audit) => {
                var _a;
                return (React.createElement("div", { key: audit.id, style: {
                        border: '1px solid var(--static-stroke-subtle)',
                        borderRadius: '6px',
                        padding: '12px',
                    } },
                    React.createElement("div", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '8px',
                        } },
                        React.createElement("span", { style: { fontWeight: 600 } }, ((_a = audit.changedByUser) === null || _a === void 0 ? void 0 : _a.email) || '-'),
                        React.createElement("span", { style: { color: 'var(--static-content-subtle)' } }, moment(audit.changedAt).format('YYYY-MM-DD HH:mm'))),
                    React.createElement(Grid, { columns: 1, gap: 12 }, audit.entries.map((entry) => {
                        var _a, _b;
                        return (React.createElement("div", { key: entry.id, style: {
                                fontSize: '13px',
                                borderTop: '1px solid var(--static-stroke-subtle)',
                                paddingTop: '8px',
                            } },
                            React.createElement("div", null,
                                t('variant_name'),
                                ": ",
                                ((_a = entry.orderComponentVariant) === null || _a === void 0 ? void 0 : _a.name) || '-',
                                ((_b = entry.orderComponentVariant) === null || _b === void 0 ? void 0 : _b.color)
                                    ? ` (${entry.orderComponentVariant.color})`
                                    : ''),
                            React.createElement("div", null,
                                t('delivery_status_history_status_change'),
                                ":",
                                ' ',
                                statusLabel(entry.previousStatus),
                                " \u2192 ",
                                statusLabel(entry.newStatus)),
                            React.createElement("div", null,
                                t('delivery_status_history_quantity'),
                                ": ",
                                entry.quantity)));
                    }))));
            }),
            pageInfo.hasNextPage && (React.createElement(Button, { theme: "border", onClick: handleLoadMore }, t('delivery_status_history_load_more'))))));
}
export default DeliveryStatusAuditHistory;
